import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import { formatApiError, authApi } from "./api";
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, Globe, ShieldCheck,
  FileText, Network, Clock, ArrowLeft, Info, CheckCircle2,
} from "lucide-react";
import { LogoMark } from "./Logo";

const HERO = "https://static.prod-images.emergentagent.com/jobs/6e81a1f9-3916-4b76-bbad-ad2e99a330b5/images/ee312c06fa4a60f9e205e900828d47204131151ae3171a3b19e01344ccc2c9e7.jpeg";

const FEATURES = [
  { icon: FileText, label: ["Ekstraksi Data", "dari Akta"] },
  { icon: Network, label: ["Visualisasi", "Struktur Grup"] },
  { icon: Clock, label: ["Riwayat", "Kepemilikan"] },
  { icon: ShieldCheck, label: ["Aman &", "Terpercaya"] },
];

type View = "login" | "register" | "forgot";

export default function Login() {
  const { login, register } = useAuth();
  const [view, setView] = useState<View>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ msg: string } | null>(null);

  const goto = (v: View) => { setView(v); setErr(""); setSent(null); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (view === "login") await login(email.trim(), password);
      else if (view === "register") await register(email.trim(), password, name.trim() || "Konsultan");
      else {
        const res = await authApi.forgotPassword(email.trim());
        setSent({ msg: res.message });
      }
    } catch (ex: any) {
      setErr(formatApiError(ex?.detail) || "Terjadi kesalahan. Coba lagi.");
    } finally {
      setBusy(false);
    }
  };

  const isForgot = view === "forgot";

  return (
    <div className="lg-root" data-theme="light">
      <aside className="lg-hero" style={{ backgroundImage: `url(${HERO})` }}>
        <div className="lg-hero-overlay" />
        <div className="lg-hero-content">
          <div className="lg-hero-top">
            <div className="lg-logo">
              <LogoMark className="lg-logo-mark" size={34} tone="onDark" />
              <div className="lg-logo-text">
                <span className="lg-wordmark">STRUKTURKU</span>
                <span className="lg-tagline">by Abadi Premium Consultant</span>
              </div>
            </div>
            <div className="lg-rule" />
            <h1 className="lg-headline serif">
              Satu Dokumen,<br />
              <span className="lg-headline-accent">Gambaran Lebih Besar.</span>
            </h1>
            <p className="lg-desc">
              Visualisasikan struktur kepemilikan perusahaan secara otomatis dari
              dokumen akta, dengan akurat, aman, dan profesional.
            </p>
            <div className="lg-features">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div className="lg-feat" key={i}>
                    <div className="lg-feat-ic"><Icon size={20} strokeWidth={2} /></div>
                    <div className="lg-feat-lbl">{f.label[0]}<br />{f.label[1]}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="lg-hero-foot">
            <span className="lg-foot-line" />
            DARI DOKUMEN AKTA<br />MENUJU KEPUTUSAN YANG LEBIH BAIK.
          </div>
        </div>
      </aside>

      <main className="lg-form-side">
        {isForgot && (
          <button className="lg-back" onClick={() => goto("login")} data-testid="back-to-login">
            <ArrowLeft size={16} strokeWidth={2} /> Kembali ke halaman masuk
          </button>
        )}
        <div className="lg-lang" data-testid="lang-select">
          <Globe size={16} strokeWidth={2} /> Bahasa Indonesia
        </div>

        <div className="lg-form-wrap">
          <div className="lg-form-brand">
            <LogoMark className="lg-brand-mark" size={46} tone="brand" />
            <span className="lg-brand-word">STRUKTURKU</span>
            <span className="lg-brand-sub">by Abadi Premium Consultant</span>
          </div>

          <h2 className="lg-welcome serif">
            {view === "login" ? "Selamat Datang Kembali" : view === "register" ? "Buat Akun Baru" : "Lupa Password?"}
          </h2>
          <p className="lg-welcome-sub">
            {view === "login" ? "Masuk ke akun Anda untuk melanjutkan"
              : view === "register" ? "Daftar sebagai konsultan untuk mulai bekerja"
              : "Masukkan email Anda untuk menerima link reset password."}
          </p>

          {isForgot && sent ? (
            <div className="lg-forgot-done">
              <div className="lg-info" data-testid="forgot-success">
                <CheckCircle2 size={17} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{sent.msg}</span>
              </div>
              <button className="lg-secondary" onClick={() => goto("login")} data-testid="forgot-back">Kembali ke halaman masuk</button>
            </div>
          ) : (
            <form className="lg-form" onSubmit={submit}>
              {view === "register" && (
                <div className="lg-field">
                  <label>Nama</label>
                  <div className="lg-input">
                    <FileText className="lg-input-ic" size={18} strokeWidth={1.8} />
                    <input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Nama lengkap" autoComplete="name" />
                  </div>
                </div>
              )}

              <div className="lg-field">
                <label>Email</label>
                <div className="lg-input">
                  <Mail className="lg-input-ic" size={18} strokeWidth={1.8} />
                  <input data-testid="auth-email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="nama@perusahaan.com" autoComplete="username" />
                </div>
              </div>

              {!isForgot && (
                <div className="lg-field">
                  <label>Password</label>
                  <div className="lg-input">
                    <Lock className="lg-input-ic" size={18} strokeWidth={1.8} />
                    <input data-testid="auth-password" type={showPass ? "text" : "password"} required value={password}
                      onChange={(e) => setPassword(e.target.value)} placeholder="Masukkan password Anda"
                      autoComplete={view === "login" ? "current-password" : "new-password"} />
                    <button type="button" className="lg-eye" onClick={() => setShowPass((s) => !s)}
                      data-testid="toggle-password" aria-label="Tampilkan password">
                      {showPass ? <EyeOff size={18} strokeWidth={1.8} /> : <Eye size={18} strokeWidth={1.8} />}
                    </button>
                  </div>
                </div>
              )}

              {view === "login" && (
                <div className="lg-forgot-row">
                  <button type="button" className="lg-forgot" data-testid="forgot-password" onClick={() => goto("forgot")}>
                    Lupa password?
                  </button>
                </div>
              )}

              {err && <div className="lg-err" data-testid="auth-error">{err}</div>}

              <button className="lg-submit" type="submit" disabled={busy} data-testid="auth-submit">
                {busy ? "Memproses…" : (view === "login" ? "Masuk" : view === "register" ? "Daftar & Masuk" : "Kirim Link Reset")}
                {!busy && <ArrowRight size={18} strokeWidth={2.4} />}
              </button>
            </form>
          )}

          {isForgot && !sent && (
            <div className="lg-info lg-info-static" data-testid="forgot-note">
              <Info size={17} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Hanya email yang pernah terdaftar di STRUKTURKU yang akan menerima pesan untuk reset password.</div>
                <div style={{ color: "#5b6b80" }}>Jika Anda tidak menerima email, mohon periksa folder spam atau hubungi tim kami.</div>
              </div>
            </div>
          )}

          {!isForgot && (
            <>
              <div className="lg-or"><span>atau</span></div>
              {view === "login" ? (
                <button className="lg-secondary" data-testid="auth-switch-register" onClick={() => goto("register")}>
                  Buat Akun Baru
                </button>
              ) : (
                <button className="lg-secondary" data-testid="auth-switch-login" onClick={() => goto("login")}>
                  Sudah punya akun? Masuk
                </button>
              )}
              <div className="lg-secure"><ShieldCheck size={15} strokeWidth={2} /> Data Anda aman bersama kami.</div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
